import { currenciesZodSchema } from '@mint-lib/currencies-helpers';
import { z } from 'zod';

import { SETTINGS_STATUS } from '../pages/Builder/types.js';
import { ValueType } from './_campaigns/campaignBuilder.js';

const CurrencySchema = z.object({
  value: currenciesZodSchema,
  displayName: z.string(),
  sign: z.string(),
});

const FieldsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.null()]),
);

// Default value is used to support old versions of mediaplans, which can miss some fields on any level.
// That's why we also use BuyingPlanInfoResponseSchema.parse() for the VersionPage
const ItemGoalFieldSchema = z.object({
  goalValue: z.string().nullable(),
  id: z.number(),
  isPrimary: z.boolean().optional(),
  metricId: z.number(),
  metricLabel: z.string(),
  metricName: z.string().prefault(''),
  valueType: z.enum(ValueType).or(z.literal('')),
});

const RowLevelFieldsSchema = z
  .object({
    itemGoal: ItemGoalFieldSchema.nullable().prefault(null),
    activeDays: z.array(z.string()).nullable().prefault(null),
    calculatedEstimatedGoals: z
      .object({
        type: z.string(),
        value: z.string().or(z.number()),
      })
      .nullable()
      .prefault(null),
  })
  .catchall(z.union([z.string(), z.number(), ItemGoalFieldSchema, z.null()]));

const RowLevelItem = z.object({
  campaign: z.number().nullable(),
  canAdserverCostBeMadeCalculated: z.boolean().prefault(false),
  canBeDeleted: z.boolean(),
  canVatBeMadeCalculated: z.boolean().prefault(false),
  channel: z.string(),
  fields: RowLevelFieldsSchema,
  hasIntegration: z.boolean().prefault(false),
  id: z.number(),
  isConfigured: z.boolean(),
  isConfiguredForIo: z.boolean().prefault(false),
  isCopy: z.boolean(),
  isFromRecommendations: z.boolean(),
  isLockedForRecommendations: z.boolean(),
  isValidIoGroup: z.boolean().prefault(false),
  ioVersionIndex: z.number().nullable().prefault(null),
  label: z.string(),
  name: z.string(),
  order: z.number(),
  overriddenCurrency: currenciesZodSchema.nullable().prefault(null),
  parent: z.number(),
  platformMedia: z.string(),
  settingsStatusMessage: z.enum(SETTINGS_STATUS).nullable(),
  type: z.string(),
});

const PlatformLevelItem = z.object({
  canBeDeleted: z.boolean(),
  fields: FieldsSchema,
  id: z.number(),
  isConfiguredForIo: z.boolean().prefault(false),
  isCopy: z.boolean(),
  isFromRecommendations: z.boolean(),
  isGhost: z.boolean(),
  isValidIoGroup: z.boolean().prefault(false),
  label: z.string(),
  name: z.string(),
  order: z.number(),
  parent: z.number(),
  subItems: z.array(RowLevelItem),
  type: z.string(),
});

const ChannelLevelItem = z.object({
  canBeDeleted: z.boolean(),
  fields: FieldsSchema,
  id: z.number(),
  isCopy: z.boolean(),
  isFromRecommendations: z.boolean(),
  label: z.string(),
  name: z.string(),
  order: z.number(),
  parent: z.null(),
  subItems: z.array(PlatformLevelItem),
  type: z.string(),
});

export const BuyingPlanInfoResponseSchema = z.object({
  budget: z.string().nullable(),
  campaignAllocatedBudget: z.string().nullable().prefault(null),
  campaignAllocatedBudgetPercent: z.number(),
  campaignBudget: z.string().nullable(),
  campaignCollection: z.number(),
  campaignUnallocatedBudget: z.string().nullable().prefault(null),
  channelMixBudget: z.string().nullable().prefault(null),
  climatePartnerError: z.string().nullable().prefault(null),
  company: z.number(),
  currency: CurrencySchema.nullable().prefault(null),
  currentVersionId: z.number().prefault(0),
  dateFrom: z.string(),
  dateTo: z.string(),
  hasCampaignCollectionDraftOrders: z.boolean(),
  id: z.number(),
  isCo2CurrencyConverted: z.boolean(),
  isLocked: z.boolean().prefault(false),
  items: z.array(ChannelLevelItem),
  name: z.string(),
  totals: z.record(z.string(), z.string().or(z.number()).nullable()),
  version: z.string(),
  isReadyForReview: z.boolean().prefault(false),
});

const UpdateCellPayloadSchema = z
  .object({
    rowId: z.number(),
  })
  .and(
    z
      .object({
        name: z.string(),
      })
      .or(
        z.object({
          fields: z.record(
            z.string(),
            z.union([z.string(), z.number(), ItemGoalFieldSchema, z.null()]),
          ),
        }),
      ),
  );

export type BuyingPlanInfo = z.infer<typeof BuyingPlanInfoResponseSchema>;
export type BuyingPlanInfoRequestPayload = {
  buyingPlanId: number | null;
  includeFields: string[];
  multiCurrency: boolean;
};

export type UpdateCellPayload = z.infer<typeof UpdateCellPayloadSchema>;
